import { FC, MutableRefObject, ReactNode, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FlatfileProvider, Sheet, Space, useFlatfile } from '@flatfile/react';
import { convertHook } from '@flatfile/v2-shims';
import Button from './Button';
import { GlobalAppContext } from './ContextProvider';
import { FieldType } from '../../consts/flatfileFields';

interface FlatfileProps {
  csvFileData?: (string | number | boolean | undefined)[][];
  csvFileName?: string;
  flatFileDataType: string;
  flatFileFields: FieldType[];
  hideDownloadButton?: boolean;
  loading?: boolean;
  text?: string;
  flatFileSubmitHandler: (results: any) => Promise<string | void | null>;
  flatFileValidators: (record: any) => Promise<{ [key: string]: { info: { message: string; level: string }[] } }>;
  close?: () => void;
  renderTrigger?: (open: () => void) => ReactNode;
}

const FlatfileSheet: FC<FlatfileProps & { openPortalRef: MutableRefObject<(() => void) | undefined> }> = props => {
  const { openPortal } = useFlatfile();

  useEffect(() => {
    props.openPortalRef.current = openPortal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPortal]);

  const blueprint: any = {
    name: props.flatFileDataType,
    slug: props.flatFileDataType.toLowerCase(),
    fields: props.flatFileFields,
  };

  return (
    <Space
      config={{
        name: 'Space',
        metadata: {
          sidebarConfig: {
            showSidebar: false,
          },
        },
      }}
    >
      <Sheet
        config={blueprint}
        onRecordHook={((record: any) => convertHook(props.flatFileValidators as any)(record)) as any}
        onSubmit={async ({ sheet }) => {
          const data = await sheet.allData();
          if (data.records && data.records.length) {
            let results: any = [];
            data.records.forEach((record: any) => {
              let recordsResult: any = {};

              Object.entries(record.values).forEach(([key, value]: any) => {
                recordsResult[key] = value.value;
              });
              results.push(recordsResult);
            });

            await props.flatFileSubmitHandler(results);
          }
        }}
        submitSettings={{
          deleteSpaceAfterSubmit: true,
        }}
      />
    </Space>
  );
};

const FlatfileSpace: FC<FlatfileProps> = props => {
  const { flatFileLicenseKey } = useContext(GlobalAppContext);
  const openPortalRef = useRef<() => void>();

  return (
    <div className="App">
      {props.renderTrigger ? (
        props.renderTrigger(() => openPortalRef.current && openPortalRef.current())
      ) : (
        <Button
          bg="success"
          text="Open Portal"
          type="button"
          onClick={() => openPortalRef.current && openPortalRef.current()}
          width="40px"
        />
      )}
      {createPortal(
        <FlatfileProvider
          publishableKey={flatFileLicenseKey}
          config={{
            displayAsModal: true,
            closeSpace: {
              operation: 'submit',
              onClose: () => {
                if (props.close) {
                  props.close();
                }
              },
            },
          }}
        >
          <FlatfileSheet {...props} openPortalRef={openPortalRef} />
        </FlatfileProvider>,
        document.body
      )}
    </div>
  );
};

export default FlatfileSpace;
